<template>
	<div class="inputFieldContainer relative">
		<ClientOnly>
			<label
				:for="id"
				v-if="label"
				class="inputFieldLabel"
				:class="labelColor"
				>{{ label }}
				<FormRequiredAsterisk :required="required" />
				<FormTooltip
					v-if="!!tooltipMsg"
					:tooltip-msg="tooltipMsg" />
			</label>
			<button
				:id="id"
				class="inputField inline-flex items-center justify-between bg-white"
				:class="{ inputFieldError: hasError }"
				type="button"
				:disabled="disabled"
				@click="toggleOptions(true)">
				{{ placeholder }}
				<svg
					class="w-4 h-4 ml-2"
					aria-hidden="true"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M19 9l-7 7-7-7"></path>
				</svg>
			</button>
			<label
				:for="id"
				v-if="hasError && errorMessage"
				class="inputFieldErrorLabel"
				>{{ errorMessage }}</label
			>

			<!-- Dropdown menu -->
			<div
				:id="`${id}-options`"
				class="dropdownMenu top-16 w-full"
				:class="{ hidden: !showOptions }"
				ref="dropdownMenu">
				<ul
					class="dropdownMenuList"
					:aria-labelledby="id">
					<li v-if="shouldShowSearch && !disableSearch">
						<FormTextFieldWithBtn
							class="searchField"
							:key="searchFieldKey"
							:initial-value="searchFieldVal"
							:placeholder="$t('search')"
							:btn-label="'X'"
							@input="searching"
							@clicked="clearSearch" />
					</li>
					<li v-for="option in filteredOptions">
						<p
							class="dropdownMenuListItem"
							@click="selected(option)">
							{{ option.label }}
						</p>
					</li>
				</ul>
			</div>
		</ClientOnly>
	</div>
</template>

<script setup>
	import { useI18n } from "vue-i18n"
	import useDetectOutsideClick from "assets/helpers/useDetectOutsideClick.js"

	const { t } = useI18n()

	const { id, initialValue, options } = defineProps({
		id: {
			type: String,
			default: "inputField",
		},
		label: {
			type: String,
		},
		labelColor: {
			type: String,
			default: "text-primary",
		},
		required: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		hasError: {
			type: Boolean,
			default: false,
		},
		errorMessage: {
			type: String,
			default: "",
		},
		options: {
			//type of [{value, label}]
			type: Array,
			default: [],
		},
		initialValue: {
			//preselect the dropdown by the options value field
			default: "",
		},
		disableSearch: {
			type: Boolean,
			default: false,
		},
		tooltipMsg: {
			type: String,
			default: "",
		},
	})

	const searchFieldVal = ref("")
	const searchFieldKey = ref(Date.now())

	const placeholder = computed(() => {
		if (initialValue && options) {
			const initialOption = options.find((option) => option.value === initialValue)
			return !!initialOption ? initialOption.label : `${t("dropdownPlaceholder")}`
		}
		return `${t("dropdownPlaceholder")}`
	})
	const shouldShowSearch = options.length > 10 //Only show the search field if number of options is above the limit
	const filteredOptions = ref(options)

	const emits = defineEmits(["changed", "blur"])
	const selected = (option) => {
		toggleOptions(false)
		emits("changed", { id: id, value: option.value })
	}

	const blur = () => {
		toggleOptions(false)
		emits("blur", { id: id })
	}

	const showOptions = ref(false)
	const dropdownMenu = ref(null)
	const listener = useDetectOutsideClick(dropdownMenu, [id], blur)

	const toggleOptions = (show = true) => {
		showOptions.value = show

		if (show) {
			window.addEventListener("click", listener)
			window.addEventListener("touchend", listener)
			if (shouldShowSearch) {
				nextTick(() => {
					const searchField = document.querySelector("#billingState-options .searchField input")
					searchField && searchField.focus()
				})
			}
		} else {
			window.removeEventListener("click", listener)
			window.removeEventListener("touchend", listener)
		}
	}

	const searching = ({ value }) => {
		const searchTerm = value.toLowerCase()
		filteredOptions.value = options.filter((option) => option.label.toLowerCase().includes(searchTerm))
	}

	const clearSearch = () => {
		searchFieldVal.value = ""
		searchFieldKey.value = Date.now() //Change the text field key to force a rerender
		filteredOptions.value = options
	}
</script>

<style lang="scss">
	//unscoped to affect the child element
	.searchField {
		.inputField {
			@apply bg-gray-100 focus:outline-none;
		}
	}
</style>
